import React, { useState, useCallback, useEffect, useRef } from 'react';
import Keyboard from './components/Keyboard';
import DrawingPad from './components/DrawingPad';
import RichEditor from './components/RichEditor';
import AudioPanel from './components/AudioPanel';
import SharePanel from './components/SharePanel';
import { loadFromURL } from './utils/shareUtils';
import './App.css';

const TERMINAL_TITLE_ITEMS = ['ፊደል', 'አቦጊዳ', 'አቦጊዳሄውዞ'];
const THEME_STORAGE_KEY = 'fidel-board-theme';

function RetroTerminalTitle() {
  const [text, setText] = useState('');
  const textRef = useRef('');
  const deletingRef = useRef(false);
  const loopNumRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const period = 2000;

    const tick = () => {
      const i = loopNumRef.current % TERMINAL_TITLE_ITEMS.length;
      const fullTxt = TERMINAL_TITLE_ITEMS[i];

      if (deletingRef.current) {
        textRef.current = fullTxt.substring(0, textRef.current.length - 1);
      } else {
        textRef.current = fullTxt.substring(0, textRef.current.length + 1);
      }

      setText(textRef.current);

      let delta = 120 - Math.random() * 45;
      if (deletingRef.current) {
        delta /= 2;
      }

      if (!deletingRef.current && textRef.current === fullTxt) {
        delta = period;
        deletingRef.current = true;
      } else if (deletingRef.current && textRef.current === '') {
        deletingRef.current = false;
        loopNumRef.current += 1;
        delta = 450;
      }

      timeoutRef.current = setTimeout(tick, delta);
    };

    tick();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleReloadHome = (event) => {
    event.preventDefault();
    window.location.href = '/';
  };

  return (
    <a
      href="/"
      className="title-geez typewrite"
      data-period="2000"
      data-type={JSON.stringify(TERMINAL_TITLE_ITEMS)}
      onClick={handleReloadHome}
      aria-label="Reload Fidel Board home"
    >
      <span className="title-prefix">#</span>
      <span className="wrap" aria-live="polite">{text}</span>
      <span className="terminal-cursor" aria-hidden="true">█</span>
    </a>
  );
}

export default function App() {
  const [text, setText] = useState('');
  const [selectedRange, setSelectedRange] = useState({ start: 0, end: 0 });
  const [activePanel, setActivePanel] = useState('keyboard'); // keyboard | draw | share
  const [drawingActive, setDrawingActive] = useState(false);
  const [sharedAudioState, setSharedAudioState] = useState(null);
  const [themeMode, setThemeMode] = useState('dark');
  const audioStateRef = useRef(null);

  // Viewport spacers for horizontal keyboard sun-ring overflow
  const [spacers, setSpacers] = useState({ left: 0, right: 0 });
  const viewportRef = useRef(null);

  // Automatically scroll viewport when dynamic spacers are injected or removed
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    if (spacers.right > 0) {
      viewport.scrollLeft = spacers.right;
    } else if (spacers.left > 0) {
      viewport.scrollLeft = 0;
    } else {
      viewport.scrollLeft = 0;
    }
  }, [spacers]);

  useEffect(() => {
    const shared = loadFromURL();
    if (shared) {
      const restoredText = shared.text || '';
      if (shared.audioState) {
        setSharedAudioState({ ...shared.audioState, text: restoredText });
      } else {
        setText(restoredText);
        setSelectedRange({ start: restoredText.length, end: restoredText.length });
      }
    }
  }, []);

  useEffect(() => {
    try {
      const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (storedTheme === 'light' || storedTheme === 'dark') {
        setThemeMode(storedTheme);
      }
    } catch (err) {
      // Ignore storage quirks; the app still defaults to dark mode.
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    document.body.classList.toggle('light-theme', themeMode === 'light');
    document.body.classList.toggle('dark-theme', themeMode === 'dark');

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeMode === 'light' ? '#f8fafc' : '#0d0d1a');
    }

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch (err) {
      // Persisting the preference is best-effort.
    }
  }, [themeMode]);

  // Safeguard selection range against boundary overflow if text is modified externally
  useEffect(() => {
    const len = text.length;
    if (selectedRange.start > len || selectedRange.end > len) {
      const newPos = Math.min(selectedRange.start, len);
      setSelectedRange({ start: newPos, end: newPos });
    }
  }, [text, selectedRange]);

  const handleRestoreComplete = useCallback((restoredText) => {
    setText(restoredText);
    setSelectedRange({ start: restoredText.length, end: restoredText.length });
    setSharedAudioState(null);
  }, []);

  const handleGetAudioState = useCallback((getStateFn) => {
    audioStateRef.current = getStateFn;
  }, []);

  const handleThemeToggle = useCallback(() => {
    setThemeMode((currentMode) => (currentMode === 'dark' ? 'light' : 'dark'));
  }, []);

  const handleTextChange = useCallback((newText) => {
    setText(newText);
  }, []);

  const handleSelectRange = useCallback((range) => {
    setSelectedRange(range);
  }, []);

  // Core insertion engine used by all virtual typing and recognition components
  const insertTextAtCursor = useCallback((insertedText) => {
    const { start, end } = selectedRange;
    const newText = text.slice(0, start) + insertedText + text.slice(end);
    const newPos = start + insertedText.length;
    setText(newText);
    setSelectedRange({ start: newPos, end: newPos });
  }, [text, selectedRange]);

  const handleCharInput = useCallback((char) => {
    insertTextAtCursor(char);
  }, [insertTextAtCursor]);

  const handleCharRecognized = useCallback((char) => {
    insertTextAtCursor(char);
  }, [insertTextAtCursor]);

  const handleSpace = useCallback(() => {
    insertTextAtCursor('፡');
  }, [insertTextAtCursor]);

  const handleEnter = useCallback(() => {
    insertTextAtCursor('\n');
  }, [insertTextAtCursor]);

  const handleDelete = useCallback(() => {
    const { start, end } = selectedRange;
    if (start !== end) {
      // Delete selection if text is highlighted
      const newText = text.slice(0, start) + text.slice(end);
      setText(newText);
      setSelectedRange({ start, end: start });
    } else if (start > 0) {
      // Delete previous single character relative to cursor position
      const newText = text.slice(0, start - 1) + text.slice(start);
      const newPos = start - 1;
      setText(newText);
      setSelectedRange({ start: newPos, end: newPos });
    }
  }, [text, selectedRange]);

  return (
    <div ref={viewportRef} className="app-viewport">
      {/* Dynamic left spacer column */}
      {spacers.left > 0 && (
        <div className="app-spacer left-spacer" style={{ width: spacers.left }} />
      )}

      <div className="app">
        <header className="app-header">
          <h1 className="app-title">
            <RetroTerminalTitle />
            <span className="title-divider">—</span>
            <span className="title-latin">Fidel Board</span>
          </h1>
          <nav className="app-nav">
            <button
              className={`nav-btn theme-toggle-btn ${themeMode === 'light' ? 'nav-active' : ''}`}
              onClick={handleThemeToggle}
              aria-pressed={themeMode === 'light'}
              title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
            >
              <svg
                className="btn-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {themeMode === 'dark' ? (
                  <>
                    <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.8 6.8 0 0 0 9.8 9.8Z" />
                  </>
                ) : (
                  <>
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </>
                )}
              </svg>
              {themeMode === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button
              className={`nav-btn ${activePanel === 'share' ? 'nav-active' : ''}`}
              onClick={() => { setActivePanel('share'); setDrawingActive(false); }}
            >
              <svg
                className="btn-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              Share
            </button>
          </nav>
        </header>

        <main className="app-main">
          {/* Left panel — input tools */}
          <aside className="app-sidebar">
            <div className="sidebar-toggle">
              <button
                className={`sidebar-toggle-btn ${activePanel === 'keyboard' ? 'toggle-active' : ''}`}
                onClick={() => { setActivePanel('keyboard'); setDrawingActive(false); }}
              >
                <svg
                  className="btn-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" ry="2"/>
                  <line x1="6" y1="8" x2="6" y2="8"/>
                  <line x1="10" y1="8" x2="10" y2="8"/>
                  <line x1="14" y1="8" x2="14" y2="8"/>
                  <line x1="18" y1="8" x2="18" y2="8"/>
                  <line x1="6" y1="12" x2="6" y2="12"/>
                  <line x1="18" y1="12" x2="18" y2="12"/>
                  <line x1="7" y1="16" x2="17" y2="16"/>
                  <line x1="10" y1="12" x2="10" y2="12"/>
                  <line x1="14" y1="12" x2="14" y2="12"/>
                </svg>
                Keyboard
              </button>
              <button
                className={`sidebar-toggle-btn ${activePanel === 'draw' ? 'toggle-active' : ''}`}
                onClick={() => { setActivePanel('draw'); setDrawingActive(true); }}
              >
                <svg
                  className="btn-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                Draw
              </button>
            </div>

            {activePanel === 'keyboard' && (
              <Keyboard
                onCharInput={handleCharInput}
                onDelete={handleDelete}
                onSpace={handleSpace}
                onEnter={handleEnter}
                onSpacersChange={setSpacers}
              />
            )}

            {activePanel === 'draw' && (
              <DrawingPad
                onCharRecognized={handleCharRecognized}
                active={drawingActive}
              />
            )}

            {activePanel === 'share' && (
              <SharePanel
                text={text}
                onClose={() => setActivePanel('keyboard')}
                onGetAudioState={() => audioStateRef.current ? audioStateRef.current() : null}
              />
            )}
          </aside>

          {/* Right panel — text area + audio panel (always visible) */}
          <section className="app-content">
            <RichEditor
              text={text}
              onTextChange={handleTextChange}
              selectedRange={selectedRange}
              onSelectRange={handleSelectRange}
            />

            <AudioPanel 
              text={sharedAudioState ? '' : text} 
              onTextChange={handleTextChange} 
              compact={true} 
              onGetState={handleGetAudioState}
              sharedState={sharedAudioState}
              onRestoreComplete={handleRestoreComplete}
            />
          </section>
        </main>

        <footer className="app-footer">
          <span>Ge'ez Keyboard · <span className="geez-chars">ፊደል</span> · All libraries bundled · Web Speech + Web Audio + Canvas</span>
        </footer>
      </div>

      {/* Dynamic right spacer column */}
      {spacers.right > 0 && (
        <div className="app-spacer right-spacer" style={{ width: spacers.right }} />
      )}
    </div>
  );
}